import { useCallback } from 'react';
import { odata } from '@bingads-webui/http-util';
import { useOdataOptions } from './use-odata-options';

// BulkFileType enum value for AIGC document uploads (.pdf, .docx)
const BULK_FILE_TYPE_AIGC_DOCUMENT = 22;

/**
 * Document file extensions supported by the document upload API.
 */
export const SUPPORTED_DOCUMENT_EXTENSIONS = ['.pdf', '.docx'];

export interface BlobUploadInfo {
  BlobName: string;
  SasUrl: string;
}

export interface CommittedFileInfo {
  BlobName: string;
  FileName: string;
  FileExtension: string;
  Status: string;
}

/**
 * Uploads a file to Azure Blob Storage via the 3-step OData flow:
 *   1. GET GetBlobUploadUrl → { BlobName, SasUrl }
 *   2. PUT file bytes to SasUrl with x-ms-blob-type header
 *   3. POST CommitUploadedFile → { BlobName, FileName, FileExtension, Status }
 *
 * @param {Record<string, unknown>} odataOptions - OData connection options
 * @param {string} aid - Account ID
 * @param {File} file - The File object to upload
 * @returns {Promise<CommittedFileInfo>} committed file info
 */
const uploadFile = (
  odataOptions: Record<string, unknown>,
  aid: string,
  file: File
): Promise<CommittedFileInfo> => {
  const dotIndex = file.name.lastIndexOf('.');
  const fileExtension = dotIndex > 0 ? file.name.substring(dotIndex) : '';
  // CommitUploadedFile expects the bare filename (no extension); the
  // server stores extension separately and rejects names that include it.
  const fileNameWithoutExtension = dotIndex > 0 ? file.name.substring(0, dotIndex) : file.name;

  // Step 1: Get SAS URL from OData
  const getSasUrl = `/BulkUploadFiles/GetBlobUploadUrl(AccountId=${aid},BulkFileType=${BULK_FILE_TYPE_AIGC_DOCUMENT},FileExtension=${fileExtension})`;

  return odata.get<{ url: string }, BlobUploadInfo>({
    ...odataOptions,
    url: getSasUrl,
  })
    .then((blobInfo: BlobUploadInfo) => {
      const { BlobName, SasUrl } = blobInfo;

      // Step 2: PUT file bytes to Azure Blob Storage
      return fetch(SasUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Blob upload failed with status ${response.status}`);
          }
          return BlobName;
        });
    })
    .then((blobName: string) => {
      // Step 3: Commit the uploaded file
      const commitUrl = `/BulkUploadFiles/CommitUploadedFile(AccountId=${aid})`;

      return odata.post({
        ...odataOptions,
        url: commitUrl,
        data: {
          BlobName: blobName,
          FileName: fileNameWithoutExtension,
          FileType: BULK_FILE_TYPE_AIGC_DOCUMENT,
        },
      }) as Promise<CommittedFileInfo>;
    });
};

/**
 * Hook that provides a function to upload documents (.pdf, .docx) via the
 * 3-step OData + Azure Blob Storage flow for Agentic Onboarding.
 *
 * Usage:
 *   const uploadDocument = useDocumentUpload();
 *   const result = await uploadDocument(file);
 *   // result = { BlobName, FileName, FileExtension, Status }
 *
 * @returns {Function} uploadDocument - accepts a File, returns a Promise
 */
export const useDocumentUpload = (): ((file: File) => Promise<CommittedFileInfo>) => {
  const [odataOptions, { aid }] = useOdataOptions();

  return useCallback(
    (file: File) => uploadFile(odataOptions, aid, file),
    [odataOptions, aid]
  );
};
